import { type Context, createContext, type ReactNode, useContext } from "react";
import type { z } from "zod/v4";
import { loadConfig } from "./loadConfig";
import { loadVersion } from "./loadVersion";

export class Config<TSchema extends z.ZodObject<z.ZodRawShape>> {
  private readonly configSchema: TSchema;
  private readonly configPath: string;
  private readonly versionPath: string;
  private configData: z.infer<TSchema> | undefined;
  private readonly configContext: Context<z.infer<TSchema> | null>;

  constructor({
    schema,
    configPath,
    versionPath,
  }: {
    schema: TSchema;
    configPath: string;
    versionPath: string;
  }) {
    this.configSchema = schema;
    this.configPath = configPath;
    this.versionPath = versionPath;
    this.configContext = createContext<z.infer<TSchema> | null>(null);

    this.ConfigProvider = this.ConfigProvider.bind(this);
    this.TestConfigProvider = this.TestConfigProvider.bind(this);
  }

  async initConfig() {
    const [configFromFile, versionFromFile] = await Promise.all([
      loadConfig(this.configPath, this.configSchema),
      loadVersion(this.versionPath),
    ]);
    this.configData = {
      version: versionFromFile,
      ...configFromFile,
    };
  }

  getConfig() {
    if (!this.configData) {
      throw new TypeError("Config not loaded yet");
    }
    return this.configData;
  }

  useConfig() {
    const config = useContext(this.configContext);
    if (!config) {
      throw new TypeError("Config not loaded yet");
    }
    return config;
  }

  ConfigProvider({ children }: { children: ReactNode }) {
    return (
      <this.configContext.Provider value={this.getConfig()}>
        {children}
      </this.configContext.Provider>
    );
  }

  TestConfigProvider({
    config: configOverride,
    children,
    testConfig,
  }: {
    config?: Partial<z.infer<TSchema>>;
    children: ReactNode;
    testConfig: z.infer<TSchema>;
  }) {
    return (
      <this.configContext.Provider value={{ ...testConfig, ...configOverride }}>
        {children}
      </this.configContext.Provider>
    );
  }
}
